import React from "react";
import { InView } from 'react-intersection-observer';

const OurMission = () => {
  const missionPoints = [
    {
      icon: "🎯",
      title: "Exceptional Experience",
      description: "Provide customers with reliable and stress-free car rental experiences"
    },
    {
      icon: "🚗",
      title: "Diverse Vehicle Range",
      description: "Offer everything from economy cars to luxury SUVs for every traveler"
    },
    {
      icon: "⚡",
      title: "Fast & Easy Booking",
      description: "Streamlined platform with transparent pricing and no hidden charges"
    },
    {
      icon: "💫",
      title: "Customer Satisfaction",
      description: "Focus on convenience, affordability, and seamless vehicle access"
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-br from-purple-900 via-blue-800 to-cyan-700 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-15">
        <div className="absolute top-10 left-10 w-64 h-64 bg-pink-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-blue-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left Section - Enhanced Image */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div
                ref={ref}
                className={`lg:w-1/2 relative ${inView ? 'animate__animated animate__fadeInLeft' : ''}`}
              >
                <div className="relative group">
                  {/* Background Glow */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                  
                  {/* Main Image Container */}
                  <div className="relative rounded-2xl overflow-hidden border-2 border-white/20 backdrop-blur-sm shadow-2xl">
                    <img
                      src="https://www.sci.jfn.ac.lk/wp-content/uploads/2021/09/mission.gif"
                      alt="Our Mission"
                      className="w-full h-[400px] lg:h-[500px] object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                    
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-purple-900/40 to-transparent"></div>
                    
                    {/* Floating Badge */}
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white py-2 px-4 rounded-full shadow-lg transform -rotate-6">
                      <span className="text-sm font-bold">Our Purpose</span>
                    </div>
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute -bottom-3 -right-3 w-24 h-24 bg-yellow-400 rounded-full opacity-20 animate-bounce"></div>
                  <div className="absolute -top-3 -left-3 w-16 h-16 bg-cyan-400 rounded-full opacity-30 animate-pulse"></div>
                </div>
              </div>
            )}
          </InView>

          {/* Right Section - Enhanced Content */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div
                ref={ref}
                className={`lg:w-1/2 ${inView ? 'animate__animated animate__fadeInRight' : ''}`}
              >
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
                  {/* Section Header */}
                  <div className="mb-8 text-center lg:text-left">
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                      Our <span className="bg-gradient-to-r from-pink-400 to-cyan-400 bg-clip-text text-transparent">Mission</span>
                    </h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-pink-400 to-cyan-400 rounded-full mx-auto lg:mx-0"></div>
                  </div>

                  {/* Main Mission Statement */}
                  <div className="mb-8">
                    <p className="text-xl text-blue-100 leading-relaxed text-center lg:text-left">
                      At <span className="font-semibold text-cyan-300">Nupura Self Drive Cars</span>, our mission is to redefine car rental experiences by providing exceptional service that puts customers first.
                    </p>
                  </div>

                  {/* Mission Points Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {missionPoints.map((point, index) => (
                      <div 
                        key={index}
                        className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-pink-400/30 transition-all duration-300 group hover:bg-white/10"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-xl group-hover:scale-110 transition-transform duration-300">
                            {point.icon}
                          </div>
                          <div>
                            <h3 className="text-white font-bold text-lg mb-1">{point.title}</h3>
                            <p className="text-blue-100 text-sm">{point.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Additional Description */}
                  <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <p className="text-blue-100 leading-relaxed text-center lg:text-left">
                      We are committed to making every journey—whether for business, family vacations, or spontaneous road trips—easier, safer, and more enjoyable through our customer-focused approach and premium service standards.
                    </p>
                  </div>

                  {/* CTA Button */}
                  <div className="mt-8 text-center lg:text-left">
                    <button className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-3 px-8 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300">
                      Join Our Mission
                    </button>
                  </div>
                </div>

                {/* Stats Section */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="text-center bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                    <div className="text-2xl font-bold text-pink-400">10K+</div>
                    <div className="text-blue-200 text-sm">Happy Customers</div>
                  </div>
                  <div className="text-center bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                    <div className="text-2xl font-bold text-cyan-400">99%</div>
                    <div className="text-blue-200 text-sm">Satisfaction Rate</div>
                  </div>
                  <div className="text-center bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                    <div className="text-2xl font-bold text-yellow-400">24/7</div>
                    <div className="text-blue-200 text-sm">Support</div>
                  </div>
                </div>
              </div>
            )}
          </InView>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default OurMission;